import { ServiceError, unsupported } from '../errors'
import { bootstrapUrl, pmsUrl, postJson } from '../http'
import { fetchPendingToPaid, clearPendingToPaidCache, pmsDateToIso } from '../pagos/pendingToPaid'
import { pagosReal } from '../pagos/real'
import { changeResult, confirmWithPayment, pendingChange, revertChangePayment, waitForChangePayment } from './change-payment'
import type { ChangePaymentBackend } from './change-payment'
import { money, normalizeCurrency, parseMoney } from '../types'
import type { Money } from '../types'
import { derivePhase } from './phase'
import type {
  ApiCancelReserve,
  ApiCatalogOption,
  ApiCheckCancelReserve,
  ApiCheckModifyReserve,
  ApiFindReserveResponse,
  ApiListItem,
  ApiModifyReserve,
  ApiReserveDataGuestApp,
  ApiTravelerRegistered,
  CancelQuote,
  CatalogOption,
  ChangeInput,
  ChangeOption,
  ChangeQuote,
  GuideItem,
  RegistrationCatalogs,
  ReservaService,
  Stay,
  TravelerDetails,
} from './types'

/** `dd-MM-yyyy` + `HH:mm` → ISO local (inventario D25). */
export function toLocalIso(date: string, time: string): string {
  const [d, m, y] = date.split('-').map(Number) as [number, number, number]
  const [hh, mm] = (time || '00:00').split(':').map(Number) as [number, number]
  const dt = new Date(y, m - 1, d, hh, mm, 0, 0)
  const pad = (n: number) => String(n).padStart(2, '0')
  const offset = -dt.getTimezoneOffset()
  const sign = offset >= 0 ? '+' : '-'
  const abs = Math.abs(offset)
  return `${y}-${pad(m)}-${pad(d)}T${pad(hh)}:${pad(mm)}:00${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`
}

const toNumber = (s: string | undefined | null): number | null => {
  if (s === undefined || s === null || s.trim() === '') return null
  const n = Number(s.replace(',', '.'))
  return Number.isFinite(n) ? n : null
}
const orNull = (s: string | undefined | null) => (s && s.trim() !== '' ? s : null)

const toGuideItem = (it: ApiListItem): GuideItem => ({
  name: it.name,
  description: it.description ?? '',
  iconUrl: orNull(it.iconPath),
  images: it.imagePathList ?? [],
  videos: it.videoPathList ?? [],
})

/** `dd-MM-yyyy` → `yyyy-MM-dd`; null si no tiene esa forma. */
export function pmsDateToIsoDate(date: string | undefined | null): string | null {
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(date?.trim() ?? '')
  return m ? `${m[3]}-${m[2]}-${m[1]}` : null
}

const toOption = (o: ApiCatalogOption): CatalogOption => ({ id: o.id, name: o.name, value: o.value })

function toDetails(tr: ApiTravelerRegistered): TravelerDetails {
  const rel = tr.relationship
  return {
    name: orNull(tr.firstName) ?? tr.name ?? '',
    surname1: tr.surname1 ?? '',
    surname2: tr.surname2 ?? '',
    email: orNull(tr.email),
    bornDate: pmsDateToIsoDate(tr.bornDate),
    identifier: orNull(tr.identifier),
    identifierTypeId: orNull(tr.identifierTypeId),
    sexId: orNull(tr.sexId),
    countryId: orNull(tr.countryId),
    provinceId: orNull(tr.provinceId),
    municipalityId: orNull(tr.municipalityId),
    city: orNull(tr.city),
    address: orNull(tr.address),
    postalCode: orNull(tr.postalCode),
    supportNumber: orNull(tr.supportNumber),
    relationship: rel && rel.idOfAdult ? { ...rel, idOfJunior: orNull(rel.idOfJunior) } : null,
  }
}

/** Catálogos del registro de viajeros (E1), portados de los selectores de la app actual. */
export function toCatalogs(api: ApiReserveDataGuestApp): RegistrationCatalogs {
  return {
    documentCountries: (api.countryDataList ?? []).map((c) => ({
      id: c.id,
      name: c.name,
      isoCountry: orNull(c.isoCountry),
      isDefault: Boolean(c.countryDefault),
      flagUrl: orNull(c.flagPath),
      documentTypes: (c.documentTypeList ?? []).map((d) => ({
        id: d.id,
        name: d.name,
        label: d.labelName,
        scanType: d.scanType,
        front: Boolean(d.front),
        back: Boolean(d.back),
        both: Boolean(d.both),
        selfie: Boolean(d.selfie),
        scanCode: orNull(d.mobbeelCode),
      })),
    })),
    countries: (api.countryList ?? []).map(toOption),
    provinces: (api.provinceList ?? []).map(toOption),
    sexes: (api.sexList ?? []).map(toOption),
    identifierTypes: (api.identifierTypeList ?? []).map(toOption),
    relationships: (api.travelRegistrationRelationShipEnumList ?? []).map((o) => ({ ...toOption(o), type: o.type ?? '', optionA: o.optionA ?? '' })),
  }
}

/** Adapta la respuesta actual del PMS al dominio de v2. */
export function toStay(api: ApiReserveDataGuestApp, now?: Date): Stay {
  const checkIn = toLocalIso(api.iniDate, api.entryTime)
  const checkOut = toLocalIso(api.endDate, api.departureTime)
  const nights = Math.max(0, Math.round((new Date(checkOut).setHours(0, 0, 0, 0) - new Date(checkIn).setHours(0, 0, 0, 0)) / 86400000))
  const supportPhone = orNull(api.roomData?.supportPhone)
  const currency = normalizeCurrency(api.currencyCode)
  const adults = toNumber(api.adults) ?? 0
  const children = toNumber(api.children) ?? 0
  const pendingToRegister = toNumber(api.pendingToRegister) ?? 0
  return {
    id: api.id,
    guestCode: api.guestCode,
    serverPath: api.serverPath,
    reference: orNull(api.identifier) ?? api.guestCode,
    property: { name: api.roomName, primaryColor: null, logoUrl: null, coverImageUrl: null, timeZone: null },
    unit: {
      roomId: orNull(api.roomId),
      name: api.roomName,
      address: [api.roomAddress, api.provinceAndCountryAddress].filter(Boolean).join(', '),
      street: api.roomAddress ?? '',
      locality: orNull(api.provinceAndCountryAddress),
      lat: toNumber(api.coordinateY),
      lng: toNumber(api.coordinateX),
      photos: api.roomTypeImageUrlList ?? [],
      keyType: orNull(api.roomData?.keyType),
    },
    dates: { checkIn, checkOut, nights },
    guests: { adults, children, pendingToRegister },
    registration: {
      total: adults + children,
      pending: pendingToRegister,
      holderPending: Boolean(api.mainTravelRegisterPending),
      missingData: Boolean(api.missingTravelRegisterData),
      dataError: Boolean(api.errorTravelRegisterData),
      travelers: (api.travelRegisteredList ?? []).map((tr) => {
        const missing = tr.fieldsRequiered ?? []
        return {
          id: tr.id,
          name: orNull(tr.name) ?? orNull(tr.firstName) ?? '',
          surname: [tr.surname1, tr.surname2].filter(Boolean).join(' '),
          isAdult: Boolean(tr.adult),
          canDelete: Boolean(tr.canDelete),
          missingFields: missing,
          signaturePending: missing.includes('signature'),
          details: toDetails(tr),
        }
      }),
      rules: {
        forceScan: Boolean(api.forceScan),
        forceSelfie: Boolean(api.forceSelfie),
        allowFaceMatchSkip: Boolean(orNull(api.maxNumberOfScan)),
        ministerioInterior: Boolean(api.ministerioInterior),
      },
      catalogs: toCatalogs(api),
    },
    documents: (api.documentList ?? []).map((d) => ({ name: d.name, url: d.url, type: d.documentType, travelerId: orNull(d.travelRegisterId) })),
    dispositives: (api.roomData?.dispositiveList ?? []).map((d) => ({
      id: d.id,
      name: d.name,
      type: d.type,
      commands: (d.commandList ?? []).map((c) => ({ id: c.id, name: c.name, type: c.type, serverPath: c.serverPath, messageBeforeUse: orNull(c.showMessageBeforeUse) })),
      intercom: orNull(d.paso1) ? { step1: d.paso1!.trim(), label: orNull(d.interfono), step2: orNull(d.paso2) } : null,
      text: orNull(d.text),
      title: orNull(d.title),
    })),
    guide: {
      conditions: (api.conditionList ?? []).map(toGuideItem),
      manual: (api.manualList ?? []).map(toGuideItem),
      phones: (api.phoneList ?? []).filter((p) => p.number).map((p) => ({ name: p.name, number: p.number })),
    },
    bills: (api.billList ?? []).map((b) => ({ id: b.id, url: b.path })),
    channel: { name: orNull(api.channelName) },
    phase: derivePhase(checkIn, checkOut, { cancelled: api.cancelled ?? false, ...(now ? { now } : {}) }),
    contact: { whatsapp: orNull(api.hotelWhatsApp), supportPhone, emergencyPhone: supportPhone },
    wifi: { ssid: orNull(api.wifiNet) ?? orNull(api.roomData?.wifiNet), password: orNull(api.wifiPass) ?? orNull(api.roomData?.wifiPass) },
    currency,
    totalPrice: parseMoney(api.totalPrice, currency),
    flags: {
      autoCheckIn: Boolean(api.autoCheckIn),
      showDomotic: Boolean(api.showDomotic),
      showGetExtras: Boolean(api.showGetExtras),
      showGetBills: Boolean(api.showGetBills),
      allowGetBill: Boolean(api.allowGetBill),
      showGetNewReserve: Boolean(api.showGetNewReserve),
      allowModify: Boolean(api.allowModify),
      allowCancel: Boolean(api.allowcancel),
      paidWithGateway: Boolean(api.paidWithGateway),
      cardChannel: Boolean(api.cardChannel),
      previousPaidOnCard: Boolean(api.previousPaidOnCard),
      cancelled: Boolean(api.cancelled),
    },
    environmentId: api.environmentDataId,
    partner: { civitatisId: orNull(api.civitatisId), provinceId: orNull(api.roomData?.provinceId) },
  }
}

/**
 * Recorta `serverPath` al host (todo lo anterior a la primera barra), como la app actual. Si el PMS ya envía solo el host
 * (sin barra), la app actual lo deja vacío y todas las llamadas irían a `https:///…`; v2 conserva el host (fase 4, A14).
 */
export const hostOf = (serverPath: string) => /^.+?(?=\/)/.exec(serverPath)?.[0] ?? serverPath

/** `yyyy-MM-dd` → `dd-MM-yyyy` (formato de E11/E12, `format(date, 'dd-MM-yyyy')` en la app actual). */
export function isoDateToPms(date: string): string {
  const [y, m, d] = date.split('-')
  return `${d}-${m}-${y}`
}

/**
 * Importe de E11/E13. La app actual extrae la cifra con la expresión `[\d.,]` repetida porque puede venir con la moneda
 * ("225,00 €"); aquí igual, y después la normalización común (D25).
 */
export function parsePmsAmount(value: string | undefined | null, currency: string): Money | null {
  const digits = /[\d.,]+/.exec(value ?? '')?.[0]
  return digits ? parseMoney(digits, currency) : null
}

/** Tipos de cambio que la API actual cubre: fechas y noches (E11/E12). El resto no tiene endpoint (D36). */
const DATE_TYPES = new Set<ChangeInput['type']>(['dates', 'nights'])

/** Entradas de "Cambiar la reserva" con la API actual: fechas, noches y cancelar según `allowModify`/`allowcancel`. */
export function realChangeOptions(stay: Stay): ChangeOption[] {
  const base = { price: null, time: null, maxGuests: null }
  const modify = { allowed: stay.flags.allowModify, blockedBy: stay.flags.allowModify ? null : ('property' as const), ...base }
  return [
    { type: 'dates', ...modify },
    { type: 'nights', ...modify },
    { type: 'cancel', allowed: stay.flags.allowCancel, blockedBy: stay.flags.allowCancel ? null : 'property', ...base },
  ]
}

/** Adapta E11 a la cotización. Sin `quoteId`, coste del cambio, lo ya pagado ni motivo: la API no los da. */
export function toChangeQuote(api: ApiCheckModifyReserve, input: Extract<ChangeInput, { type: 'dates' | 'nights' }>, currency: string): ChangeQuote {
  const data = api.reserveModifiedData
  const empty: ChangeQuote = {
    input,
    quoteId: null,
    expiresAt: null,
    available: false,
    busy: null,
    alternatives: [],
    newCheckIn: null,
    newCheckOut: null,
    nights: null,
    unitName: null,
    newPrice: null,
    currentPrice: null,
    alreadyPaid: null,
    pendingAfter: null,
    changeFee: null,
    difference: money(0, currency),
    reason: null,
    conditions: null,
    confirmable: false,
    sendsReceipt: false,
    notifiesChannel: false,
    refundEtaDays: null,
  }
  if (!data?.allowModification) return empty
  const newPrice = parsePmsAmount(data.price, currency)
  const currentPrice = parsePmsAmount(data.oldPrice, currency)
  const difference = money((newPrice?.amount ?? 0) - (currentPrice?.amount ?? 0), currency)
  const nights = Number(data.nights)
  return {
    ...empty,
    available: true,
    newCheckIn: toLocalIso(isoDateToPms(input.checkIn), data.entryHour ?? ''),
    newCheckOut: toLocalIso(isoDateToPms(input.checkOut), data.departureHour ?? ''),
    nights: Number.isFinite(nights) && data.nights ? nights : null,
    newPrice,
    currentPrice,
    difference,
    pendingAfter: parsePmsAmount(data.pendingPrice, currency),
    conditions: data.conditions && data.conditions.trim() !== '' ? data.conditions : null,
    // D45: con o sin cobro, como la app actual (E12 y después el pago de lo pendiente).
    confirmable: true,
  }
}

/** Adapta E13. Lo que la API no da (regla con porcentaje y fecha, fianza, plazo de devolución, alternativa) queda null. */
export function toCancelQuote(api: ApiCheckCancelReserve, currency: string): CancelQuote {
  const data = api.reserveModifiedData ?? {}
  return {
    paid: parsePmsAmount(data.paidPrice, currency),
    penalty: parsePmsAmount(data.cancelPrice, currency),
    refund: parsePmsAmount(data.refundPrice, currency),
    penaltyRule: null,
    depositReleased: null,
    refundEtaDays: null,
    conditions: data.conditions && data.conditions.trim() !== '' ? data.conditions : null,
    changeDatesFreeUntil: null,
  }
}


/** E12 `modifyreserve_guestapp` (ConsultSuccessView.tsx): la reserva devuelta sustituye a la anterior. */
export async function modifyReserve(stay: Stay, checkIn: string, checkOut: string): Promise<Stay> {
  const data = await postJson<ApiModifyReserve>(pmsUrl(stay.serverPath, 'modifyreserve_guestapp'), { iniDate: isoDateToPms(checkIn), endDate: isoDateToPms(checkOut), reserveId: stay.id }, { auth: 'partner' })
  clearPendingToPaidCache()
  if (!data.ok) throw new ServiceError('server', data.statusType, data.status)
  const reserve = data.reserveDataGuestAppResponseList?.[0]
  if (!reserve) throw new ServiceError('server', 'modifyreserve without reserve')
  return toStay({ ...reserve, serverPath: hostOf(reserve.serverPath) })
}

/** Cambio con coste en el orden de la app actual y sus mitigaciones (D45, change-payment.ts). */
const realBackend: ChangePaymentBackend = {
  pagos: pagosReal,
  apply(stay, quote) {
    const { input } = quote
    if (!('checkIn' in input) || !DATE_TYPES.has(input.type)) return unsupported()
    return modifyReserve(stay, input.checkIn, input.checkOut)
  },
  revert: (stay, pending) => modifyReserve(stay, pending.originalCheckIn, pending.originalCheckOut),
  getStay: (guestCode, lang) => reservaReal.getStay(guestCode, lang),
}

/**
 * Portado de src/api/findreserve_guestapp/findreserve_guestapp.ts de la app actual: POST al host de
 * arranque con { guestCode, userLang }; `status === 'NOT_FOUND'` → enlace inválido; `serverPath` se
 * recorta al host (todo lo anterior a la primera barra).
 */
export const reservaReal: ReservaService = {
  async getStay(guestCode, lang, signal) {
    const data = await postJson<ApiFindReserveResponse>(bootstrapUrl('findreserve_guestapp_v2'), { guestCode, userLang: lang }, { auth: 'web', ...(signal ? { signal } : {}) })
    if (!data.ok) {
      if (data.status === 'NOT_FOUND') throw new ServiceError('link_invalid', data.error, data.status)
      throw new ServiceError('server', data.error, data.status)
    }
    const reserve = data.reserveDataGuestAppResponseList[0]
    if (!reserve) throw new ServiceError('link_invalid')
    return toStay({ ...reserve, serverPath: hostOf(reserve.serverPath) })
  },

  // Política con lo que da E2 (misma llamada compartida que pagos y fianza); entradas según los flags de E1.
  async getChangeOptions(stay, lang, signal) {
    const api = await fetchPendingToPaid(stay, lang, signal)
    if (!api.ok) throw new ServiceError('server', api.error ?? api.message, api.status)
    return {
      policy: {
        changeFreeUntil: null,
        freeCancel: Boolean(api.freeCancel),
        cancelFreeUntil: pmsDateToIso(api.freeCancelLimitDate),
        refundPercentAfter: null,
        conditions: api.rateConditions && api.rateConditions.trim() !== '' ? api.rateConditions : null,
      },
      options: realChangeOptions(stay),
      canRequest: false,
      requestResponseHours: null,
    }
  },

  // Sin endpoint y sin datos de ejemplo en una reserva real (D36).
  getAvailability: unsupported,
  getUnitChoices: unsupported,
  getTimeChoices: unsupported,

  // ConsultOrCancelView.tsx: E11 con las fechas en dd-MM-yyyy; `ok: false` es error; `allowModification: false`, no se puede.
  async quoteChange(stay, input, _lang, signal) {
    if (!DATE_TYPES.has(input.type) || !('checkIn' in input)) throw new ServiceError('not_supported')
    const data = await postJson<ApiCheckModifyReserve>(
      pmsUrl(stay.serverPath, 'checkmodifyreserve_guestapp'),
      { reserveId: stay.id, iniDate: isoDateToPms(input.checkIn), endDate: isoDateToPms(input.checkOut) },
      { auth: 'partner', ...(signal ? { signal } : {}) },
    )
    if (!data.ok) throw new ServiceError('server', data.statusType, data.status)
    return toChangeQuote(data, input, stay.currency)
  },

  // ConsultSuccessView.tsx (E12) y, si queda algo pendiente, PaymentTotal.tsx (E2 → E3 → banco → E4). D45.
  confirmChange: (stay, quote, card, lang) => confirmWithPayment(realBackend, stay, quote, card, lang),
  waitForChange: (stay, changeId, options) => waitForChangePayment(realBackend, stay, changeId, options),
  getChangeResult: (stay, changeId, lang) => changeResult(realBackend, stay, changeId, lang),
  revertChange: (stay, changeId, lang) => revertChangePayment(realBackend, stay, changeId, lang),
  getPendingChange: (stay, lang, signal) => pendingChange(realBackend, stay, lang, signal),

  // CancelPreviewView.tsx: E13 al entrar; los importes pueden traer la moneda.
  async quoteCancel(stay, _lang, signal) {
    const data = await postJson<ApiCheckCancelReserve>(pmsUrl(stay.serverPath, 'checkcancelreserve_guestapp'), { reserveId: stay.id }, { auth: 'partner', ...(signal ? { signal } : {}) })
    if (!data.ok) throw new ServiceError('server', data.statusType, data.status)
    return toCancelQuote(data, stay.currency)
  },

  // CancelPreviewView.tsx: E14; `ok: false` es error.
  async cancel(stay) {
    const data = await postJson<ApiCancelReserve>(pmsUrl(stay.serverPath, 'cancelreserve_guestapp'), { reserveId: stay.id }, { auth: 'partner' })
    clearPendingToPaidCache()
    if (!data.ok) throw new ServiceError('server', data.statusType, data.status)
  },

  // Sin endpoint de peticiones: la pantalla ofrece el contacto con la propiedad (D36).
  requestChange: unsupported,
}
